import React, { useState, useEffect } from 'react';
import { useUsers } from '../context/UsersContext';
import { useClients } from '../context/ClientsContext';
import HealthCuesLogo from './HealthCuesLogo';
import UserProfileDropdown from './UserProfileDropdown';
import Icon from './Icon';
import { C } from '../utils/styles';
import api from '../utils/api';
import BankingStep from './employer/BankingStep';
import CensusUploadStep from './employer/CensusUploadStep';
import PayrollSetupStep from './employer/PayrollSetupStep';
import EnrollmentStep from './employer/EnrollmentStep';
import EmployerDashboard from './portal/EmployerDashboard';
import SavedOptionsStickyStrip from './portal/SavedOptionsStickyStrip';
import MyTrainingHome from './learner/MyTrainingHome';
import MandatoryTrainingBanner from './learner/MandatoryTrainingBanner';

export default function EmployerPortal({ gatingStatus }) {
  const { currentUser, logout } = useUsers();
  const [portalTab, setPortalTab] = useState('dashboard'); // 'dashboard' | 'training'
  const [client, setClient] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Hooks must all be declared before any conditional returns (React rules-of-hooks)
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistStep, setChecklistStep] = useState('contracts');

  useEffect(() => {
    loadClientData();
  }, [currentUser]);

  const loadClientData = async () => {
    if (!currentUser || !currentUser.clients || currentUser.clients.length === 0) {
      setLoading(false);
      return;
    }

    try {
      // Get client data - employer contacts have their clientId in clients[0]
      const clientId = currentUser.clients[0];
      const clientData = await api.clients.get(clientId);
      setClient(clientData);

      // Get contracts
      const contractsData = await api.clients.getContracts(clientId);
      setContracts(contractsData);
    } catch (error) {
      console.error('Failed to load client data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: C.bg, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ fontSize: 14, color: C.textMid }}>Loading...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: C.bg, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: C.textMid, marginBottom: 12 }}>
            Unable to load your company information.
          </div>
          <button
            onClick={logout}
            style={{
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              color: C.blue,
              background: 'transparent',
              border: `1px solid ${C.blue}`,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  const companyName = client.company?.legalName || client.company?.dba || 'Your Company';
  const onboarding = client.onboarding || {};

  const handleOpenChecklist = (stepId) => {
    setChecklistStep(stepId || 'contracts');
    setChecklistOpen(true);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: C.bg, 
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" 
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      
      {/* Header */}
      <div style={{ 
        background: '#fff', 
        borderBottom: `1px solid ${C.border}`,
        padding: '16px 24px'
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <HealthCuesLogo size={32} />
            <div style={{ 
              fontSize: 16, 
              fontWeight: 700, 
              color: C.text 
            }}>
              {companyName}
            </div>
            {/* Portal section tabs */}
            <div style={{ display: 'flex', gap: 4, marginLeft: 16, background: C.bg, borderRadius: 8, padding: 4 }}>
              {[
                { id: 'dashboard', label: 'Dashboard', icon: 'home-outline' },
                { id: 'training',  label: 'Training',  icon: 'school-outline' },
              ].map((t) => {
                const isActive = portalTab === t.id;
                const hasPending = t.id === 'training' && gatingStatus?.blocked;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`employer-tab-${t.id}`}
                    onClick={() => setPortalTab(t.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: 'none',
                      background: isActive ? '#fff' : 'transparent',
                      color: isActive ? C.accent : C.textMid,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: "'Inter', sans-serif",
                      boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                      position: 'relative',
                    }}
                  >
                    <Icon name={t.icon} style={{ fontSize: 14 }} />
                    {t.label}
                    {hasPending && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange, display: 'inline-block' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          <UserProfileDropdown />
        </div>
      </div>

      {/* Saved Options Sticky Strip — only on the dashboard view */}
      {portalTab === 'dashboard' && (
        <SavedOptionsStickyStrip
          client={client}
          onCompare={() => {
            const el = document.getElementById('saved-options-anchor');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}

      {/* Main Content */}
      <div style={{ 
        maxWidth: 1200, 
        margin: '0 auto',
        padding: '32px 24px 48px'
      }}>
        {/* Soft training banner — only on the dashboard view */}
        {portalTab === 'dashboard' && (
          <MandatoryTrainingBanner onGoToTraining={() => setPortalTab('training')} />
        )}

        {portalTab === 'dashboard' && (
          <EmployerDashboard
            client={client}
            currentUser={currentUser}
            onOpenChecklist={handleOpenChecklist}
          />
        )}
        {portalTab === 'training' && (
          <MyTrainingHome embedded={true} />
        )}
      </div>

      {/* Onboarding Checklist Modal */}
      {checklistOpen && (
        <OnboardingChecklistModal
          client={client}
          contracts={contracts}
          onboarding={onboarding}
          currentUser={currentUser}
          onRefresh={loadClientData}
          initialStep={checklistStep}
          onClose={() => setChecklistOpen(false)}
        />
      )}
    </div>
  );
}

// Wraps the existing OnboardingMode in a modal/drawer for use from the dashboard.
function OnboardingChecklistModal({ client, contracts, onboarding, currentUser, onRefresh, initialStep, onClose }) {
  // Lock body scroll while modal open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(12, 24, 48, 0.5)', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
      <div style={{
        background: '#fff',
        width: '100%',
        maxWidth: 1100,
        margin: '24px',
        borderRadius: 12,
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 48px)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderBottom: `1px solid ${C.border}`, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Onboarding Checklist</div>
          <button type="button" onClick={onClose} data-testid="close-checklist-modal" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, color: C.textMid, padding: 4 }}>
            <Icon name="close" />
          </button>
        </div>
        <div style={{ padding: '24px' }}>
          <OnboardingMode
            client={client}
            contracts={contracts}
            onboarding={onboarding}
            currentUser={currentUser}
            onRefresh={onRefresh}
            initialStep={initialStep}
          />
        </div>
      </div>
    </div>
  );
}

function OnboardingMode({ client, contracts, onboarding, currentUser, onRefresh, initialStep }) {
  const [activeStep, setActiveStep] = useState(initialStep || 'contracts');
  const [censusPayrollTab, setCensusPayrollTab] = useState('census'); // Track which sub-tab is active within Step 3

  const steps = [
    { id: 'contracts', label: 'Contracts', order: 1 },
    { id: 'banking', label: 'Banking', order: 2 },
    { id: 'census', label: 'Census & Payroll', order: 3 },
    { id: 'enrollment', label: 'Enrollment', order: 4 },
    { id: 'goLive', label: 'Go Live', order: 5 },
  ];

  const getStepStatus = (stepId) => {
    return onboarding.steps?.[stepId]?.status || 'not_started';
  };

  const completedSteps = steps.filter(s => getStepStatus(s.id) === 'complete').length;
  const progressPercent = Math.round((completedSteps / steps.length) * 100);

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, marginBottom: 8 }}>
          Welcome, {currentUser.firstName}! Let's get {client.company?.legalName} set up.
        </h1>
        <div style={{ fontSize: 14, color: C.textMid, marginBottom: 16 }}>
          Your onboarding is {progressPercent}% complete.
        </div>
        <div style={{ 
          height: 8, 
          background: C.border, 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          <div style={{ 
            height: '100%',
            width: `${progressPercent}%`,
            background: C.green,
            transition: 'width 0.3s'
          }} />
        </div>
        <div style={{ 
          fontSize: 12, 
          color: C.textLight, 
          marginTop: 8 
        }}>
          {completedSteps} of {steps.length} steps complete
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ 
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32,
        marginBottom: 32
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          gap: 16
        }}>
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const isComplete = status === 'complete';
            const isActive = status === 'in_progress' || step.id === activeStep;
            const isPending = status === 'not_started';

            return (
              <React.Fragment key={step.id}>
                <div 
                  onClick={() => setActiveStep(step.id)}
                  style={{ 
                    flex: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    opacity: isPending ? 0.6 : 1
                  }}
                >
                  <div style={{ 
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: isComplete ? C.green : isActive ? C.blue : C.border,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 8px',
                    fontSize: 16,
                    fontWeight: 700,
                    animation: isActive && !isComplete ? 'pulse 2s infinite' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isComplete ? <Icon name="checkmark" style={{ fontSize: 18 }} /> : `0${index + 1}`}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                    {step.label}
                  </div>
                  <div style={{ fontSize: 10, color: C.textLight, marginTop: 4 }}>
                    {isComplete ? 'Complete' : isActive ? 'In Progress' : 'Pending'}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div style={{ 
                    width: 40,
                    height: 2,
                    background: isComplete ? C.green : C.border,
                    marginBottom: 40
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Current Step Content */}
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ 
          fontSize: 14, 
          fontWeight: 700, 
          color: C.textLight,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 16
        }}>
          CURRENT STEP
        </h2>
        
        {activeStep === 'contracts' && (
          <ContractsStep 
            contracts={contracts} 
            client={client}
            currentUser={currentUser}
            onRefresh={onRefresh}
          />
        )}
        
        {activeStep === 'banking' && (
          <BankingStep 
            client={client}
            onComplete={() => {
              onRefresh();
              setActiveStep('census');
            }}
          />
        )}
        
        {activeStep === 'census' && (
          <div>
            {/* Sub-step tabs for Census & Payroll */}
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              marginBottom: 24,
              borderBottom: `2px solid ${C.border}`,
              paddingBottom: 12
            }}>
              <button
                onClick={() => setCensusPayrollTab('census')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 20px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  background: censusPayrollTab === 'census' ? C.accent : 'transparent',
                  color: censusPayrollTab === 'census' ? '#fff' : C.textMid,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  position: 'relative',
                  bottom: -2,
                  borderBottom: censusPayrollTab === 'census' ? `3px solid ${C.accent}` : 'none',
                }}
              >
                <span>3a. Census</span>
                {client.employees?.length > 0 && (
                  <Icon name="checkmark-circle" style={{ fontSize: 16, color: censusPayrollTab === 'census' ? '#fff' : C.green }} />
                )}
                {!client.employees?.length && (
                  <span style={{ 
                    fontSize: 10, 
                    background: censusPayrollTab === 'census' ? 'rgba(255,255,255,0.2)' : C.orangeLight,
                    color: censusPayrollTab === 'census' ? '#fff' : C.orange,
                    padding: '2px 6px', 
                    borderRadius: 4,
                    fontWeight: 700
                  }}>
                    Pending
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setCensusPayrollTab('payroll')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 20px',
                  borderRadius: '8px 8px 0 0',
                  border: 'none',
                  background: censusPayrollTab === 'payroll' ? C.accent : 'transparent',
                  color: censusPayrollTab === 'payroll' ? '#fff' : C.textMid,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  position: 'relative',
                  bottom: -2,
                  borderBottom: censusPayrollTab === 'payroll' ? `3px solid ${C.accent}` : 'none',
                }}
              >
                <span>3b. Payroll</span>
                {client.payroll && (
                  <Icon name="checkmark-circle" style={{ fontSize: 16, color: censusPayrollTab === 'payroll' ? '#fff' : C.green }} />
                )}
                {!client.payroll && (
                  <span style={{ 
                    fontSize: 10, 
                    background: censusPayrollTab === 'payroll' ? 'rgba(255,255,255,0.2)' : C.orangeLight,
                    color: censusPayrollTab === 'payroll' ? '#fff' : C.orange,
                    padding: '2px 6px', 
                    borderRadius: 4,
                    fontWeight: 700
                  }}>
                    Pending
                  </span>
                )}
              </button>
            </div>
            
            {/* Tab content */}
            {censusPayrollTab === 'census' && (
              <CensusUploadStep 
                client={client}
                onComplete={() => {
                  onRefresh();
                  // Check if payroll is also complete - if so, move to enrollment
                  if (client.payroll) {
                    setActiveStep('enrollment');
                  } else {
                    // Just switch to payroll tab to prompt user
                    setCensusPayrollTab('payroll');
                  }
                }}
              />
            )}
            
            {censusPayrollTab === 'payroll' && (
              <PayrollSetupStep 
                client={client}
                onComplete={() => {
                  onRefresh();
                  // Check if census is also complete - if so, move to enrollment
                  if (client.employees?.length > 0) {
                    setActiveStep('enrollment');
                  } else {
                    // Just switch to census tab to prompt user
                    setCensusPayrollTab('census');
                  }
                }}
              />
            )}
          </div>
        )}
        
        {activeStep === 'enrollment' && (
          <EnrollmentStep
            client={client}
            currentUser={currentUser}
            onComplete={() => {
              onRefresh();
              setActiveStep('goLive');
            }}
            onRefresh={onRefresh}
          />
        )}
        
        {activeStep === 'goLive' && (
          <PlaceholderStep stepId={activeStep} team={onboarding.team} />
        )}
      </div>

      {/* HealthCues Team */}
      {onboarding?.team && onboarding.team.length > 0 && (
        <div>
          <h2 style={{ 
            fontSize: 14, 
            fontWeight: 700, 
            color: C.textLight,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 8
          }}>
            YOUR HEALTHCUES TEAM
          </h2>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 16 }}>
            Your support for this part of the process is:
          </p>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 16
          }}>
            {onboarding.team.map(member => (
              <div key={member.email} style={{ 
                background: '#fff',
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: 20
              }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                  {member.name}
                </div>
                <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
                  {member.role}
                </div>
                <div style={{ fontSize: 12, color: C.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="call-outline" style={{ fontSize: 14 }} />
                  {member.phone}
                </div>
                <div style={{ fontSize: 12, color: C.blue, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="mail-outline" style={{ fontSize: 14 }} />
                  {member.email}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

function ContractsStep({ contracts, client, currentUser, onRefresh }) {
  const [showSignModal, setShowSignModal] = useState(null);
  
  const getStatusBadge = (status) => {
    const badges = {
      'pending_preparation': { icon: 'time-outline', text: 'Pending', color: C.textLight },
      'draft': { icon: 'create-outline', text: 'Sign Now', color: C.blue }, // Draft = ready to sign
      'ready_for_signature': { icon: 'create-outline', text: 'Sign Now', color: C.blue },
      'viewed': { icon: 'eye-outline', text: 'Viewed', color: C.textMid },
      'signed': { icon: 'checkmark-circle', text: 'Signed', color: C.green },
      'countersigned': { icon: 'checkmark-done-circle', text: 'Fully Executed', color: C.green },
      'expired': { icon: 'alert-circle-outline', text: 'Expired', color: C.orange },
    };
    
    return badges[status] || badges.pending_preparation;
  };

  // Hardcoded contact for Contracts step
  const team = {
    name: 'Angie Comer',
    title: 'Director of Operations/Contracts',
    email: 'a.comer@healthcues.com',
    phone: '678.522.9988'
  };

  return (
    <div>
      <div style={{ 
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 32
      }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          STEP 1: Sign Your Contracts
        </h2>
        <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
          All documents are available for electronic signature below. Review each document carefully before signing.
        </p>

        {team && (
          <div style={{ 
            fontSize: 12, 
            color: C.textMid,
            marginBottom: 24,
            paddingBottom: 24,
            borderBottom: `1px solid ${C.border}`
          }}>
            Your contact for this step:<br />
            <strong>{team.name}</strong> · {team.title}<br />
            {team.email} · {team.phone}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {contracts.map(contract => {
            const badge = getStatusBadge(contract.status);
            // Allow signing if: ready_for_signature, viewed, OR if document exists but is in draft/pending
            const canSign = contract.status === 'ready_for_signature' 
                         || contract.status === 'viewed'
                         || (contract.status === 'pending_preparation' && contract.documentUrl) // Document exists but marked pending
                         || contract.status === 'draft'; // Draft status means ready but not yet marked
            
            
            return (
              <div key={contract.id} style={{ 
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: 16,
                background: C.bg
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <Icon name="document-text-outline" style={{ fontSize: 20, color: C.text }} />
                      <span style={{ fontSize: 14, fontWeight: 700 }}>
                        {contract.templateName}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.textMid, marginLeft: 28 }}>
                      {contracts.find(c => c.templateId === contract.templateId)?.notes || 
                        (contract.status === 'pending_preparation' 
                          ? '⏳ Being prepared by HealthCues — you\'ll be notified when ready for signature.'
                          : contract.status === 'signed' 
                          ? `Signed by ${contract.signedBy?.name} on ${new Date(contract.signedAt).toLocaleDateString()}`
                          : ''
                        )
                      }
                    </div>
                  </div>
                  
                  <div>
                    {canSign ? (
                      <button
                        onClick={() => setShowSignModal(contract)}
                        style={{
                          padding: '8px 16px',
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#fff',
                          background: badge.color,
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <Icon name={badge.icon} style={{ fontSize: 16 }} />
                        {badge.text}
                      </button>
                    ) : (
                      <div style={{ 
                        padding: '8px 16px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: badge.color,
                        background: C.bg,
                        border: `1px solid ${badge.color}`,
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                        <Icon name={badge.icon} style={{ fontSize: 16 }} />
                        {badge.text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ 
          fontSize: 12, 
          color: C.textLight, 
          marginTop: 24,
          paddingTop: 24,
          borderTop: `1px solid ${C.border}`
        }}>
          {contracts.filter(c => c.status === 'signed').length} of {contracts.length} contracts signed
        </div>
      </div>

      {showSignModal && (
        <SignContractModal 
          contract={showSignModal}
          client={client}
          currentUser={currentUser}
          onClose={() => setShowSignModal(null)}
          onSigned={() => {
            setShowSignModal(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

function SignContractModal({ contract, client, currentUser, onClose, onSigned }) {
  const [agreed, setAgreed] = useState(false);
  const [signatureType, setSignatureType] = useState('typed');
  const [typedSignature, setTypedSignature] = useState(currentUser.firstName + ' ' + currentUser.lastName);
  const [signerTitle, setSignerTitle] = useState(currentUser.title || '');
  const [signing, setSigning] = useState(false);
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  // Canvas drawing handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
    setHasDrawnSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawnSignature(false);
  };

  const handleSign = async () => {
    if (!agreed || signing) return;

    // Validate signature
    if (signatureType === 'typed' && !typedSignature.trim()) {
      alert('Please enter your name for the signature');
      return;
    }
    
    if (signatureType === 'drawn' && !hasDrawnSignature) {
      alert('Please draw your signature');
      return;
    }

    setSigning(true);

    try {
      let signatureData = typedSignature;
      
      // If drawn signature, convert canvas to base64
      if (signatureType === 'drawn') {
        const canvas = canvasRef.current;
        signatureData = canvas.toDataURL('image/png');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/contracts/${contract.id}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          contractId: contract.id,
          signatureType: signatureType,
          signatureData: signatureData,
          signerName: typedSignature,
          signerTitle: signerTitle,
          signerEmail: currentUser.email
        })
      });

      if (response.ok) {
        onSigned();
      } else {
        alert('Failed to sign contract. Please try again.');
      }
    } catch (error) {
      console.error('Error signing contract:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{ 
        background: '#fff',
        borderRadius: 12,
        maxWidth: 800,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: 32
      }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="document-text-outline" style={{ fontSize: 24, color: C.accent }} />
          {contract.templateName}
        </h2>

        {/* Document Content */}
        <div style={{ 
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: 24,
          marginBottom: 24,
          maxHeight: 400,
          overflow: 'auto',
          fontSize: 13,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap'
        }}>
          {contract.documentContent}
        </div>

        {/* Agreement Checkbox */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span style={{ fontSize: 13 }}>I have read and agree to the terms of this document</span>
        </label>

        {/* Signature Fields */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Signed by:</div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, color: C.textLight, display: 'block', marginBottom: 4 }}>
                Full Name
              </label>
              <input 
                type="text"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                style={{
                  width: '100%',
                  padding: 8,
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4
                }}
              />
            </div>
            
            <div>
              <label style={{ fontSize: 11, color: C.textLight, display: 'block', marginBottom: 4 }}>
                Title
              </label>
              <input 
                type="text"
                value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                placeholder="e.g., HR Director"
                style={{
                  width: '100%',
                  padding: 8,
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: C.textLight, marginBottom: 4 }}>
              Company: {client.company?.legalName || client.company?.dba}
            </div>
            <div style={{ fontSize: 11, color: C.textLight, marginBottom: 8 }}>
              Date: {new Date().toLocaleDateString()}
            </div>
          </div>

          {/* Signature Type Selector */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: C.textLight, display: 'block', marginBottom: 8 }}>
              Signature Method
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setSignatureType('typed')}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: signatureType === 'typed' ? '#fff' : C.text,
                  background: signatureType === 'typed' ? C.blue : 'transparent',
                  border: `1px solid ${signatureType === 'typed' ? C.blue : C.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon name="text-outline" style={{ fontSize: 16 }} />
                Type Name
              </button>
              <button
                type="button"
                onClick={() => setSignatureType('drawn')}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: signatureType === 'drawn' ? '#fff' : C.text,
                  background: signatureType === 'drawn' ? C.blue : 'transparent',
                  border: `1px solid ${signatureType === 'drawn' ? C.blue : C.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon name="brush-outline" style={{ fontSize: 16 }} />
                Draw Signature
              </button>
            </div>
          </div>

          {/* Signature Input Area */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: C.textLight, display: 'block', marginBottom: 4 }}>
              Signature
            </label>
            
            {signatureType === 'typed' ? (
              <div style={{ 
                padding: 16,
                border: `2px solid ${C.border}`,
                borderRadius: 4,
                background: '#fff',
                fontFamily: "'Dancing Script', cursive",
                fontSize: 24,
                minHeight: 60,
                display: 'flex',
                alignItems: 'center'
              }}>
                {typedSignature}
              </div>
            ) : (
              <div>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  style={{
                    width: '100%',
                    height: 150,
                    border: `2px solid ${C.border}`,
                    borderRadius: 4,
                    background: '#fff',
                    cursor: 'crosshair',
                    touchAction: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={clearCanvas}
                  style={{
                    marginTop: 8,
                    padding: '6px 12px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.text,
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap" rel="stylesheet" />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              color: C.text,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          
          <button
            onClick={handleSign}
            disabled={!agreed || (signatureType === 'typed' && !typedSignature) || (signatureType === 'drawn' && !hasDrawnSignature) || signing}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: agreed && ((signatureType === 'typed' && typedSignature) || (signatureType === 'drawn' && hasDrawnSignature)) && !signing ? C.blue : C.border,
              border: 'none',
              borderRadius: 6,
              cursor: agreed && ((signatureType === 'typed' && typedSignature) || (signatureType === 'drawn' && hasDrawnSignature)) && !signing ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="create-outline" style={{ fontSize: 16 }} />
            {signing ? 'Signing...' : 'Sign Document'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlaceholderStep({ stepId, team }) {
  const stepInfo = {
    banking: {
      icon: 'business-outline',
      title: 'ACH & Banking Setup',
      description: 'Configure your banking information for automated payments and deductions.',
    },
    census: {
      icon: 'people-outline',
      title: 'Census & Payroll Integration',
      description: 'Upload your employee census and connect your payroll system.',
    },
    enrollment: {
      icon: 'clipboard-outline',
      title: 'Employee Enrollment',
      description: 'Review and approve employee enrollments in the Section 125 plan.',
      contact: {
        name: 'Kate Hawn',
        title: 'Director of Enrollment',
        email: 'k.hawn@healthcues.com',
        phone: '678.522.9988'
      }
    },
    goLive: {
      icon: 'rocket-outline',
      title: 'Go Live',
      description: 'Final review and activation of your HealthCues plan.',
    }
  };

  const info = stepInfo[stepId] || {};

  return (
    <div style={{ 
      background: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: 48,
      textAlign: 'center'
    }}>
      <Icon name="construct-outline" style={{ fontSize: 48, color: C.orange, marginBottom: 16 }} />
      <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        {info.title}
      </h3>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24, maxWidth: 500, margin: '0 auto 24px' }}>
        {info.description}
      </p>
      <div style={{ fontSize: 13, color: C.textLight }}>
        This step will be available soon.
      </div>

      {info.contact && (
        <div style={{ 
          marginTop: 32,
          paddingTop: 32,
          borderTop: `1px solid ${C.border}`,
          fontSize: 12,
          color: C.textMid
        }}>
          Your contact for this step:<br />
          <strong style={{ color: C.text }}>{info.contact.name}</strong> · {info.contact.title}<br />
          {info.contact.email} · {info.contact.phone}
        </div>
      )}
    </div>
  );
}

function OperationsMode({ client }) {
  const dashboardItems = [
    { icon: 'document-text-outline', title: 'Contracts', desc: 'View signed documents' },
    { icon: 'business-outline', title: 'Banking', desc: 'Manage ACH details' },
    { icon: 'people-outline', title: 'Census', desc: 'View employees & enrollment' },
    { icon: 'analytics-outline', title: 'Savings', desc: 'View your FICA savings' },
    { icon: 'cash-outline', title: 'Payroll', desc: 'Deduction files & status' },
    { icon: 'headset-outline', title: 'Support', desc: 'Contact your HC team' },
  ];

  return (
    <div style={{ 
      background: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: 48,
      textAlign: 'center'
    }}>
      <Icon name="checkmark-circle" style={{ fontSize: 64, color: C.green, marginBottom: 16 }} />
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>
        Onboarding Complete!
      </h2>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 32 }}>
        Welcome to your HealthCues Employer Dashboard.<br />
        {client.company?.legalName}
      </p>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        maxWidth: 800,
        margin: '0 auto 32px'
      }}>
        {dashboardItems.map(item => (
          <div key={item.title} style={{ 
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: 20,
            textAlign: 'center'
          }}>
            <Icon name={item.icon} style={{ fontSize: 32, color: C.blue, marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{item.title}</div>
            <div style={{ fontSize: 11, color: C.textMid }}>{item.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, color: C.textLight }}>
        These features are being built and will be available soon.
      </div>
    </div>
  );
}
